type Layer = { id: string; close: () => void; busy: () => boolean; alive: boolean; registered: boolean; timer?: number };
const layers: Layer[] = [];
const marker = "estet-next-layer";
let travelling = false;
let nextRoute: (() => void) | undefined;
let installed = false;
const changed = () => window.dispatchEvent(new Event("estet:layers-changed"));
export const hasBackLayer = () => layers.some(layer => layer.alive);
export function routeHistoryState(state: Record<string, unknown> | null) {
  const clean = { ...state }; delete clean[marker]; return clean;
}
function beginPendingLayers() {
  if (travelling || nextRoute) return;
  for (const layer of layers) if (layer.alive && !layer.registered) {
    clearTimeout(layer.timer);
    layer.registered = true;
    history.pushState({ ...history.state, [marker]: layer.id }, "", location.href);
  }
  changed();
}
function finishTravel() {
  travelling = false;
  removeClosedEntries();
  if (travelling) return;
  const route = nextRoute; nextRoute = undefined;
  if (route) route();
  beginPendingLayers();
}
function travel(delta: number) {
  travelling = true;
  window.addEventListener("popstate", finishTravel, { once: true });
  history.go(delta);
}
function removeClosedEntries() {
  if (travelling) return;
  let count = 0;
  while (layers.length && !layers.at(-1)!.alive) {
    const layer = layers.pop()!;
    clearTimeout(layer.timer);
    if (layer.registered) count++;
  }
  if (count) travel(-count);
}
function install() {
  if (installed) return;
  installed = true;
  window.addEventListener("popstate", () => {
    if (travelling) return;
    const index = layers.findIndex(layer => layer.registered && layer.id === history.state?.[marker]);
    const removed = layers.slice(index + 1);
    const count = removed.filter(layer => layer.registered).length;
    if (removed.some(layer => layer.alive && layer.busy())) { if (count) travel(count); return; }
    layers.splice(index + 1);
    for (const layer of removed.reverse()) { clearTimeout(layer.timer); if (layer.alive) { layer.alive = false; layer.close(); } }
    changed(); removeClosedEntries();
  });
}
export function requestLayerBack() {
  if (travelling) return true;
  const layer = [...layers].reverse().find(layer => layer.alive);
  if (!layer) return false;
  if (!layer.busy()) {
    if (layer.registered) history.back();
    else { layer.alive = false; clearTimeout(layer.timer); layer.close(); removeClosedEntries(); changed(); }
  }
  return true;
}
/** Serialize route changes after removing the exact registered layer entries. */
export function navigateAfterLayers(route: () => void) {
  if (layers.some(layer => layer.alive && layer.busy())) return;
  const opened = layers.filter(layer => layer.alive);
  for (const layer of layers) { layer.alive = false; clearTimeout(layer.timer); }
  nextRoute = route;
  for (const layer of opened.reverse()) layer.close();
  changed();
  if (travelling) return;
  removeClosedEntries();
  if (!travelling) { const next = nextRoute; nextRoute = undefined; next?.(); }
}
export function registerBackLayer(close: () => void, busy: () => boolean) {
  install();
  const layer: Layer = { id: crypto.randomUUID(), close, busy, alive: true, registered: false };
  layers.push(layer);
  // StrictMode may clean up before this task; no history entry is then created.
  layer.timer = window.setTimeout(beginPendingLayers, 0);
  changed();
  return () => {
    if (!layers.includes(layer)) return;
    layer.alive = false; clearTimeout(layer.timer);
    // Parent and child unmounts are coalesced before moving browser history.
    queueMicrotask(removeClosedEntries); changed();
  };
}
